import React, { useState } from "react";
import { ButtonWithIconLoader } from "@src/components/atoms/buttons/button-with-icon-loader/button-with-icon-loader";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import styles from "./two-factor-authentication-buttons-wrapper.module.scss";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import deleteRedIcon from "@src/assets/svg/delete-red-icon.svg";
import undoIcon from "@src/assets/svg/undo.svg";
import { resendInvitationEmailUseCase } from "@src/cloud-connect/user-manager/use-cases/resend-invitation-email-use-case";
import { ZitadelUser } from "@src/types/user-management";
import { remove2FAUseCase } from "@src/cloud-connect/user-manager/use-cases/remove-2fa-use-case";
import { useI18N } from "@src/language/language-context";
const cssClassName = "two-factor-authentication-buttons-container";

interface TwoFactorAuthenticationButtonsContainerProps {
  user: ZitadelUser;
}

export const TwoFactorAuthenticationButtonsContainer = ({
  user,
}: TwoFactorAuthenticationButtonsContainerProps) => {
  const i18n = useI18N();

  const isUserInitialOrEmailNotVerified =
    user.status === "INITIAL" || user.email_is_verified === false;

  const texts = {
    resendEmailSuccess: i18n(
      "modules.twoFactorAuthenticationButtonsWrapper.resendEmailSuccess",
    ),
    resendEmailError: i18n(
      "modules.twoFactorAuthenticationButtonsWrapper.resendEmailError",
    ),
    remove2FASuccess: i18n(
      "modules.twoFactorAuthenticationButtonsWrapper.remove2FASuccess",
    ),
    remove2FAError: i18n(
      "modules.twoFactorAuthenticationButtonsWrapper.remove2FAError",
    ),
    manageTwoFactorAuthenticationLabel: i18n(
      "modules.twoFactorAuthenticationButtonsWrapper.manageTwoFactorAuthenticationLabel",
    ),
    bothOptionsActiveText: i18n(
      "modules.twoFactorAuthenticationButtonsWrapper.bothOptionsActiveText",
    ),
    removeTwoFactorAuthenticationText: i18n(
      "modules.twoFactorAuthenticationButtonsWrapper.removeTwoFactorAuthenticationText",
    ),
    resendEmailButtonText: i18n(
      "modules.twoFactorAuthenticationButtonsWrapper.resendEmailButtonText",
    ),
    remove2FAButtonText: i18n(
      "modules.twoFactorAuthenticationButtonsWrapper.remove2FAButtonText",
    ),
  };

  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [isResetting2FA, setIsResetting2FA] = useState(false);

  const handleResendEmail = async () => {
    setIsResendingEmail(true);
    const response = await resendInvitationEmailUseCase(user.id);

    if (response.success) {
      successSnackbar(texts.resendEmailSuccess);
    } else {
      errorSnackbar(response.error || texts.resendEmailError);
    }
    setIsResendingEmail(false);
  };

  const handleReset2FA = async () => {
    setIsResetting2FA(true);
    const response = await remove2FAUseCase(user.id);
    if (response.success) {
      successSnackbar(texts.remove2FASuccess);
    } else {
      errorSnackbar(response.error || texts.remove2FAError);
    }
    setIsResetting2FA(false);
  };

  return (
    <div className={styles[cssClassName]}>
      <p className={styles[`${cssClassName}__label`]}>
        {texts.manageTwoFactorAuthenticationLabel}
      </p>
      <div className={styles[`${cssClassName}__wrapper`]}>
        {isUserInitialOrEmailNotVerified ? (
          <div className={styles[`${cssClassName}__text`]}>
            {texts.bothOptionsActiveText}
          </div>
        ) : (
          <div className={styles[`${cssClassName}__text`]}>
            {texts.removeTwoFactorAuthenticationText}
          </div>
        )}
        <FlexRowContainer horizontalGap={8} justifyContent={"flex-start"}>
          {isUserInitialOrEmailNotVerified && (
            <ButtonWithIconLoader
              fun={handleResendEmail}
              icon={undoIcon}
              text={texts.resendEmailButtonText}
              variant={["no-outline", "color-blue"]}
              loading={isResendingEmail}
            />
          )}
          <ButtonWithIconLoader
            fun={handleReset2FA}
            icon={deleteRedIcon}
            text={texts.remove2FAButtonText}
            variant={["no-outline", "color-red"]}
            loading={isResetting2FA}
          />
        </FlexRowContainer>
      </div>
    </div>
  );
};
